"use client";

import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  HStack,
} from "@chakra-ui/react";
import { Campaign } from "../voice-services/campaignService";
import { useAuth } from "../contexts/AuthContext";
import { uploadFileToS3 } from "../utils/s3Upload";

interface RunCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
}

const RunCampaignModal: React.FC<RunCampaignModalProps> = ({
  isOpen,
  onClose,
  campaign,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const toast = useToast();
  const { credits, user } = useAuth();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
    } else {
      toast({
        title: "Please select a valid CSV file",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleRunNow = async () => {
    if (!csvFile || !campaign) {
      toast({
        title: "Please upload a CSV file",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "User not authenticated",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsRunning(true);
    try {
      // Step 1: Upload CSV to S3
      toast({
        title: "Uploading file to S3...",
        status: "info",
        duration: 2000,
      });
      
      const s3Result = await uploadFileToS3(csvFile, user.id);
      
      // Step 2: Send S3 key to backend
      toast({
        title: "Processing leads...",
        status: "info",
        duration: 2000,
      });

      const response = await fetch(
        `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:8000"
        }/api/campaigns/${campaign.campaign_id}/upload-leads-s3`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": user.id,
          },
          body: JSON.stringify({ csvS3Key: s3Result.key }),
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Server response:", errorData);
        throw new Error(
          `Failed to upload leads: ${response.status} - ${errorData}`
        );
      }

      const result = await response.json();
      toast({
        title: `Campaign started with ${result.leads_count} leads`,
        status: "success",
        duration: 5000,
      });
      onClose();
    } catch (error) {
      console.error("Error starting campaign:", error);
      toast({
        title: "Error starting campaign",
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleSchedule = () => {
    toast({
      title: "Schedule feature coming soon",
      status: "info",
      duration: 3000,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Run Campaign: {campaign?.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Upload a CSV file with your leads to start the campaign.
            </Text>
            <FormControl>
              <FormLabel>Upload CSV File</FormLabel>
              <VStack spacing={2} align="stretch">
                <HStack>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    p={1}
                    flex={1}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="primary"
                    color="primary.700"
                    _hover={{ color: "primary.900" }}
                    onClick={() => {
                      const csvContent =
                        "first_name,last_name,email,phone,company";
                      const blob = new Blob([csvContent], { type: "text/csv" });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = "leads_template.csv";
                      link.click();
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    Download Template
                  </Button>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  Need help? Download the template to see the required format:
                  first_name, last_name, email, phone, company
                </Text>
              </VStack>
            </FormControl>
            {csvFile && (
              <Text fontSize="sm" color="green.500">
                File selected: {csvFile.name}
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              color="black"
              _hover={{ bg: "gray.100" }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              onClick={handleSchedule}
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to schedule campaign"
                  : undefined
              }
            >
              Schedule for Later
            </Button>
            <Button
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              onClick={handleRunNow}
              isLoading={isRunning}
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to run campaign"
                  : undefined
              }
            >
              Run Now
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RunCampaignModal;
